import { useEffect, useRef, useCallback } from 'react';
import styles from './Testimonials.module.css';
import DesktopTestimonials from '../../../website-desktop/components/Page6/DesktopTestimonials';

const QUOTE_ICON = '/assets/quote-open-green.svg';

function ChevronLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

const TESTIMONIALS = [
  {
    name: 'Fr. Bharath Reddy',
    role: 'Principal, STEM School, Guntur',
    photo: '/assets/testimonial-bharat-reddy.png',
    quote: 'After adopting SchoolAi, children are getting support in completing homework, revising concepts, and clearing doubts, helping them go beyond the textbook.',
  },
  {
    name: 'Ms. Manisha Joshi',
    role: 'Principal, Mother Mary\'s School, Delhi',
    photo: '/assets/testimonial-manisha-joshi.png',
    quote: 'Through SchoolAi, our students receive real personalised learning support and doubt clarification 24/7. Something that was not possible before in any means.',
  },
  {
    name: 'Dr. Rupamala Singh',
    role: 'Principal, Hayde Heritage Academy, Uttarakhand',
    photo: '/assets/testimonial-rupamala-singh.png',
    quote: 'Parents at our school now do not worry about extra tuition, as students are getting the support they need from SchoolAi.',
  },
];

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add(styles.visible);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

function useDraggableScroll() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.innerWidth >= 769) return;
    const ele = ref.current;
    if (!ele) return;

    let pos = { top: 0, left: 0, x: 0, y: 0 };
    let isDragging = false;

    const mouseDownHandler = function (e) {
      isDragging = true;
      pos = {
        left: ele.scrollLeft,
        top: ele.scrollTop,
        x: e.clientX,
        y: e.clientY,
      };
      ele.style.cursor = 'grabbing';
      ele.style.userSelect = 'none';
    };

    const mouseMoveHandler = function (e) {
      if (!isDragging) return;
      const dx = e.clientX - pos.x;
      const dy = e.clientY - pos.y;
      ele.scrollTop = pos.top - dy;
      ele.scrollLeft = pos.left - dx;
    };

    const mouseUpHandler = function () {
      isDragging = false;
      ele.style.cursor = 'grab';
      ele.style.removeProperty('user-select');
    };

    ele.addEventListener('mousedown', mouseDownHandler);
    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);

    return () => {
      ele.removeEventListener('mousedown', mouseDownHandler);
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
    };
  }, []);
  return ref;
}

export default function Testimonials() {
  const headerRef = useReveal(0.2);
  const scrollRef = useDraggableScroll();
  const wrapRef = useRef(null);

  // Arrow navigation — scroll the track by one card (+ gap) at a time
  const scrollByCards = useCallback((dir) => {
    const el = wrapRef.current;
    if (!el) return;
    const card = el.querySelector(`.${styles.card}`);
    const amount = card ? card.offsetWidth + 70 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  }, []);

  return (
    <section className={styles.section} aria-label="Testimonials">
      {/* Header */}
      <div ref={headerRef} className={styles.header}>
        <h2 className={styles.heading}>Testimonials</h2>
        <div className={styles.connector} aria-hidden="true" />
      </div>

      {/* Horizontal scroll cards — arrows flank on desktop */}
      <div className={styles.carouselOuter}>
        <button
          type="button"
          className={`${styles.navArrow} ${styles.navPrev}`}
          onClick={() => scrollByCards(-1)}
          aria-label="Previous testimonials"
        >
          <ChevronLeft />
        </button>

        <div className={styles.scrollWrap} ref={wrapRef}>
          <div className={styles.scrollTrack} ref={scrollRef} style={{ cursor: 'grab' }}>
            {TESTIMONIALS.map((t, i) => (
              <article key={i} className={styles.card}>
              {/* Top row: photo + name/role */}
              <div className={styles.cardTop}>
                <div className={styles.photoWrap}>
                  {t.photo ? (
                    <img src={t.photo} alt={t.name} className={styles.photo} loading="lazy" />
                  ) : (
                    <span className={styles.photoInitial}>{t.name.charAt(0)}</span>
                  )}
                </div>
                <div className={styles.cardMeta}>
                  <span className={styles.cardName}>{t.name}</span>
                  <span className={styles.cardRole}>{t.role}</span>
                </div>
              </div>

              {/* Quote */}
              <div className={styles.quoteBlock}>
                <img src={QUOTE_ICON} alt="" width="14" height="12" aria-hidden="true" className={styles.quoteIcon} />
                <p className={styles.cardQuote}>{t.quote}</p>
              </div>
            </article>
          ))}
          </div>
        </div>

        <button
          type="button"
          className={`${styles.navArrow} ${styles.navNext}`}
          onClick={() => scrollByCards(1)}
          aria-label="Next testimonials"
        >
          <ChevronRight />
        </button>
      </div>
    </section>
  );
}
